import styled from 'styled-components';
import { FOCUS_HALO_REACH } from '../../assets/styles/components/scrollGutter';

/** A px number, or `gutter` to track the responsive page-padding token. */
export type ListSpacing = number | 'gutter';

export const resolveListSpacing = (spacing: ListSpacing) =>
  spacing === 'gutter' ? 'var(--spacing-gutter)' : `${spacing}px`;

const NEGATIVE_HALO_REACH = `calc(-1 * ${FOCUS_HALO_REACH})`;

const HALO_AND_SCROLLBAR_REACH = `calc(${NEGATIVE_HALO_REACH} - var(--scrollbar-width))`;

interface ListScrollAreaProperties {
  $contentGap: ListSpacing;
  $headerGap: ListSpacing;
}

export const ListScrollArea = styled.div<ListScrollAreaProperties>`
  && {
    width: auto;
  }

  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  /* the task pane is embedded in Outlook; never chain a bounce to the host */
  overscroll-behavior: contain;
  /* keeps header and cards the same width whether or not the list overflows */
  scrollbar-gutter: stable;
  padding-bottom: var(--spacing-gutter);
  padding-inline: ${FOCUS_HALO_REACH};
  margin-inline-start: ${NEGATIVE_HALO_REACH};
  margin-inline-end: ${HALO_AND_SCROLLBAR_REACH};

  &.scroll-page-gutter {
    padding-top: 0;

    margin-inline-end: ${HALO_AND_SCROLLBAR_REACH};

    /* The gutter rides whichever block is still on screen once the header collapses: the header
       itself when a pinned row outlives the collapse, the collapsible part otherwise. */
    .list-header:has(.list-header-pinned) {
      padding-top: var(--spacing-gutter);
    }

    .list-header:not(:has(.list-header-pinned)) .list-header-collapsible {
      padding-top: var(--spacing-gutter);
    }
  }

  .list-header {
    position: sticky;
    top: 0;
    z-index: 4;
    flex-shrink: 0;
    background-color: var(--clr-body-bg);
    transition: transform 0.2s ease-in-out;

    &[data-collapsed='true'] {
      transform: translateY(calc(-1 * var(--list-header-collapsible-height, 0px)));
    }

    &:has([data-testid='filter-button'].active),
    &:has([aria-expanded='true']) {
      transform: none;
    }
  }

  .list-header-collapsible,
  .list-header-pinned {
    padding-bottom: ${({ $headerGap }) => resolveListSpacing($headerGap)};
  }

  .list-header-collapsible:empty {
    padding-bottom: 0;
  }

  .list-content {
    display: flex;
    flex-direction: column;
    /* grow so an empty state fills the pane; never shrink below the cards */
    flex: 1 0 auto;
    gap: ${({ $contentGap }) => resolveListSpacing($contentGap)};
  }

  /* opt-in target for useScrollToDate's scrollIntoView */
  .scroll-anchor {
    scroll-margin-top: var(--list-header-height, 0px);
  }

  @media (prefers-reduced-motion: reduce) {
    .list-header {
      transition: none;
    }
  }
`;
